import { ToastContext } from '@/contexts/ToastContext'
import useIsMobile from '@/hooks/useIsMobile'
import { HeadingLabel, MetaDataLabel } from '@/styles/fonts'
import { GrandstandIcon } from '@grandstand-web/bally-web-shared/src/components/GrandstandIcon'
import { AnalyticServiceContext } from '@grandstand-web/bally-web-shared/src/services/analytics/AnalyticService'
import { useTealium } from '@grandstand-web/bally-web-shared/src/services/tealium/TealiumService'
import { UserServiceContext } from '@grandstand-web/bally-web-shared/src/services/user/UserService'
import { isValidZipcode } from '@grandstand-web/bally-web-shared/src/utils/stringUtils'
import { ActiveSubscriptionsResponse } from '@grandstand/presentation-models'
import { useRouter } from 'next/router'
import { useContext, useState } from 'react'
import styled from 'styled-components'
import { GrandstandButton } from '../Buttons'
import Tooltip from '../Tooltip'
import { AccountDeletionInitiated, DeleteAccount } from './DeleteAccount'
import UpdateField from './UpdateField'

type AccountDetailsProps = {
  subscriptions: ActiveSubscriptionsResponse
  onBack: () => void
}

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
`

const Header = styled(HeadingLabel)`
  border-bottom: 1px solid var(--divider-canvas);
  padding-bottom: 25px;
  padding-left: 25px;
  padding-top: 25px;
  div {
    float: left;
    top: 3px;
    right: 10px;
    position: relative;
    @media (min-width: 768px) {
      display: none;
    }
  }
`

const FieldDiv = styled.div`
  cursor: pointer;
  border-bottom: 1px solid var(--divider-canvas);
  padding-left: 25px;
  padding-right: 25px;
  height: 70px;
  div {
    position: relative;
  }
`
const FieldDivEmail = styled(FieldDiv)`
  cursor: initial;
  svg {
    cursor: pointer;
  }
`

const FieldHomeZip = styled(FieldDiv)<{ zipCodeLocked: boolean }>`
  cursor: ${(props) => (props.zipCodeLocked ? 'initial' : 'pointer')};
  svg {
    cursor: pointer;
  }
`

const FieldLabel = styled(MetaDataLabel)`
  top: 25px;
`

const FieldInfo = styled(MetaDataLabel)`
  color: var(--on-background-alt);
  float: right;
  top: 5px;
  display: flex;
  div {
    top: -2px;
  }
`

const FieldInfoDetail = styled(MetaDataLabel)`
  color: var(--on-background-alt);
  padding-right: 24px;
`

const RefreshWrapper = styled.div``

const SignOutWrapper = styled.div`
  div {
    position: relative;
    top: 60px;
    right: 30px;
  }
`

const ArrowIcon = styled(GrandstandIcon)``

const FieldIcon = styled(GrandstandIcon)``

const Spinner = styled.div`
  border: 6px solid var(--canvas);
  border-top: 6px solid var(--on-background);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`
const ToolTipLink = styled.a`
  text-decoration: underline;
  cursor: pointer;
`
const EmailTooltipChildren = () => {
  const tealium = useTealium()
  return (
    <div>
      You cannot change your email on the app. Please contact{' '}
      <ToolTipLink href="https://help.fanduelsportsnetwork.com" target="_blank" onClick={() => tealium.changeEmail()}>
        customer care
      </ToolTipLink>{' '}
      to make changes.
    </div>
  )
}

const ZipCodeTooltipChildren = () => {
  return (
    <div>
      You cannot change your Home zipcode on the app. Please contact{' '}
      <ToolTipLink href="https://help.fanduelsportsnetwork.com" target="_blank">
        customer care
      </ToolTipLink>{' '}
      to make changes.
    </div>
  )
}

const AccountDetails = ({ onBack, subscriptions }: AccountDetailsProps) => {
  const [fieldToShow, setFieldToShow] = useState('main')
  const tealium = useTealium()
  const [currentFieldName, setCurrentFieldName] = useState('')
  const [currentFieldData, setCurrentFieldData] = useState('')
  const [refreshingZipCode, setRefreshingZipCode] = useState(false)
  const userService = useContext(UserServiceContext)
  const analyticService = useContext(AnalyticServiceContext)
  const deleteStatus = userService.currentUser?.profile.internal?.delete_status
  const name = userService.currentUser?.profile.internal?.name
  const email = userService.currentUser?.profile.internal?.email
  const zipCode = userService.currentUser?.profile.region?.zip_code
  const zipCodeLocked = userService.currentUser?.profile.region?.is_locked
  const regionCity = userService.currentUser?.profile?.region.city ?? ''
  const regionState = userService.currentUser?.profile?.region.state ?? ''
  const deviceZipCode = userService.deviceRegion?.zip_code ?? ''
  const deviceCity = userService.deviceRegion?.city ?? ''
  const deviceState = userService.deviceRegion?.state ?? ''
  const router = useRouter()
  const isMobile = useIsMobile()
  const userToken = userService.currentUser?.user_token
  const toastService = useContext(ToastContext)

  const handleUpdateField = async (fieldName: string, newValue: any) => {
    try {
      if (fieldName === 'Name') {
        await userService.updateName(newValue)
      } else if (fieldName === 'Zip Code') {
        if (isValidZipcode(newValue) && newValue !== userService.currentUser?.profile?.region.zip_code) {
          if (!userService.currentUser?.profile?.region.is_locked) {
            userService.updateZipcode(newValue)
          } else {
            alert(
              `Your profile is locked to zip code ${userService.currentUser?.profile?.region.zip_code}. Please contact support if you wish to unlock your zip code.`
            )
          }
        }
      } else if (fieldName === 'Password' && userToken) {
        userService.updatePassword(newValue, userToken)
      }
      handleOnSuccess()
    } catch (error) {
      console.error('Error updating field:', error)
      toastService.showToast({
        state: 'error',
        iconLeft: true,
        children: 'Something went wrong, please try again',
      })
    }
  }

  const handleCancelUpdate = () => {
    setFieldToShow('main')
  }

  const handleBackClick = () => {
    if (isMobile) {
      router.push('/settings')
      onBack()
    }
  }

  const handleOnSuccess = () => {
    toastService.showToast({
      state: 'success',
      iconLeft: true,
      children: 'Successfully updated',
    })
    setFieldToShow('main')
  }

  const handleOnClick = (fieldHeader: string, data: string) => () => {
    setCurrentFieldData(data)
    setCurrentFieldName(fieldHeader)
    setFieldToShow('update')
  }

  const handleSignOut = () => {
    userService.logOut()
  }

  const handleClickDelete = () => {
    if (deleteStatus === 'pending_email_verify') {
      setFieldToShow('delete-initiated')
    } else if (deleteStatus === 'email_verified') {
      router.push('/deleteAccount')
    } else if (deleteStatus === undefined) {
      setFieldToShow('delete')
    }
  }

  const handleRefreshZipCode = async () => {
    if (refreshingZipCode) {
      return
    }
    try {
      setRefreshingZipCode(true)
      await userService.refreshDeviceRegion()
    } catch (e) {
      console.error(e)
    } finally {
      setTimeout(() => {
        setRefreshingZipCode(false)
      }, 6000)
    }
  }

  return (
    <>
      {fieldToShow === 'main' && name && email && zipCode && (
        <MainContent>
          <Header onClick={handleBackClick} fontSize="4">
            <ArrowIcon icon="arrow_left" /> Account Details
          </Header>
          <FieldDiv onClick={handleOnClick('Name', name)}>
            <FieldLabel fontSize="sm">Name</FieldLabel>
            <FieldInfo fontSize="sm">
              <FieldInfoDetail fontSize="sm">{name}</FieldInfoDetail> <FieldIcon icon="chevron_right" />
            </FieldInfo>
          </FieldDiv>
          <FieldDivEmail>
            <FieldLabel fontSize="sm">Email</FieldLabel>
            <FieldInfo fontSize="sm">
              <FieldInfoDetail fontSize="sm">{email}</FieldInfoDetail>{' '}
              <Tooltip tooltipChildren={<EmailTooltipChildren />}>
                <FieldIcon icon="help" />
              </Tooltip>
            </FieldInfo>
          </FieldDivEmail>
          <FieldDiv onClick={handleOnClick('Password', 'Enter new password')}>
            <FieldLabel fontSize="sm">Password</FieldLabel>
            <FieldInfo fontSize="sm">
              <FieldIcon icon="chevron_right" />
            </FieldInfo>
          </FieldDiv>
          <FieldDiv onClick={handleClickDelete}>
            <FieldLabel fontSize="sm">Delete Account</FieldLabel>
            <FieldInfo fontSize="sm">
              {deleteStatus === 'email_verified' ? (
                <FieldInfoDetail fontSize="sm">Finalize Delete</FieldInfoDetail>
              ) : (
                deleteStatus === 'pending_email_verify' && (
                  <FieldInfoDetail fontSize="sm">Process Initiated</FieldInfoDetail>
                )
              )}
              <FieldIcon icon="chevron_right" />
            </FieldInfo>
          </FieldDiv>
          <Header fontSize="3">Location</Header>
          <FieldHomeZip
            zipCodeLocked={zipCodeLocked ?? false}
            onClick={zipCodeLocked ? () => {} : handleOnClick('Zip Code', zipCode)}
          >
            <FieldLabel fontSize="sm">Home Zip Code</FieldLabel>
            <FieldInfo fontSize="sm">
              <FieldInfoDetail fontSize="sm">{`${zipCode} ${regionCity}, ${regionState}`}</FieldInfoDetail>{' '}
              {!zipCodeLocked ? (
                <FieldIcon icon="chevron_right" />
              ) : (
                <Tooltip tooltipChildren={<ZipCodeTooltipChildren />}>
                  <FieldIcon icon="help" />
                </Tooltip>
              )}
            </FieldInfo>
          </FieldHomeZip>
          <FieldHomeZip zipCodeLocked={true}>
            <FieldLabel fontSize="sm">Current Zip Code</FieldLabel>
            <FieldInfo fontSize="sm">
              <FieldInfoDetail fontSize="sm">{`${deviceZipCode} ${deviceCity}, ${deviceState}`}</FieldInfoDetail>{' '}
              <div onClick={handleRefreshZipCode}>{refreshingZipCode ? <Spinner /> : <FieldIcon icon="refresh" />}</div>
            </FieldInfo>
          </FieldHomeZip>
          <SignOutWrapper>
            <FieldInfo fontSize="sm">
              <GrandstandButton onClick={handleSignOut} buttonStyle="alt" buttonSize="xs">
                SIGN OUT
              </GrandstandButton>
            </FieldInfo>
          </SignOutWrapper>
        </MainContent>
      )}
      {fieldToShow === 'update' && (
        <UpdateField
          currentFieldData={currentFieldData}
          fieldName={currentFieldName}
          onSuccess={handleUpdateField}
          onCancel={handleCancelUpdate}
        />
      )}
      {fieldToShow === 'delete' && (
        <DeleteAccount subscriptions={subscriptions} setFieldToShow={setFieldToShow} returnBack={handleOnSuccess} />
      )}
      {fieldToShow === 'delete-initiated' && (
        <AccountDeletionInitiated
          subscriptions={subscriptions}
          setFieldToShow={setFieldToShow}
          returnBack={handleOnSuccess}
        />
      )}
    </>
  )
}

export default AccountDetails
